import { runtimeConfig } from "@/config/env";
import { cookieRefresh } from "@/lib/auth-api";
import { resolveSessionUser } from "@/lib/auth-session";
import { useAuthStore } from "@/stores/auth-store";

export interface HttpRequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
  noAuth?: boolean;
  _isRetry?: boolean;
}

function buildUrl(path: string, params?: HttpRequestOptions["params"]): string {
  const url = new URL(path, runtimeConfig.adminApiUrl);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined) return;
      url.searchParams.append(key, String(value));
    });
  }
  return url.toString();
}

/**
 * A failed HTTP response, carrying the STATUS as well as the message.
 *
 * Every call site that only reads `.message` is unaffected: this is still an
 * Error and the message is unchanged. The status exists because some failures
 * need to be told apart by kind rather than by matching on server prose: the
 * machine-tag review RPC refuses service principals and sessions without
 * editorial access with a 403, and a queue that renders that as an ordinary
 * "action failed" sends the reviewer looking for a bug in the tag instead of
 * at their own session. See isAuthFailure below.
 */
export class HttpError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

/** True for the two statuses that mean "your session, not your request". */
export function isAuthFailure(error: unknown): boolean {
  return error instanceof HttpError && (error.status === 401 || error.status === 403);
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message = errorBody?.message ?? response.statusText;
    throw new HttpError(message, response.status);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const { clearSession, setSession } = useAuthStore.getState();

  try {
    const refreshed = await cookieRefresh();
    if (!refreshed) {
      clearSession();
      return null;
    }

    const user = await resolveSessionUser(refreshed.access_token);
    if (!user) {
      clearSession();
      return null;
    }

    setSession({
      user,
      session: {
        token: refreshed.access_token,
        expiresAt: refreshed.expires_at,
      },
    });

    return refreshed.access_token;
  } catch {
    clearSession();
    return null;
  }
}

/**
 * Obtain an access token to RETRY a request that just got a 401.
 *
 * It must not hand back the token from the store. That is the token the server
 * has just rejected, so returning it retries the identical expired credential,
 * takes the second 401, and gives up — leaving a session that can never heal
 * itself until the operator reloads or signs in again. The refresh branch was
 * unreachable for precisely the case it exists to handle.
 *
 * Observed in production as a steady trickle of `Unauthenticated: invalid
 * authentication token` on the two flag list RPCs. They are the sidebar badge's
 * calls, which react-query refetches on window focus — so they are the requests
 * that fire first after the operator leaves the tab idle long enough for the
 * token to expire, and the only ones that reliably catch it.
 *
 * `refreshPromise` still de-duplicates: several requests failing at once share
 * one refresh rather than racing several.
 */
async function getFreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function httpRequest<T>(path: string, options: HttpRequestOptions = {}) {
  const { _isRetry, noAuth, ...requestOptions } = options;
  const { session } = useAuthStore.getState();
  const headers = new Headers(requestOptions.headers);
  // JSON unless the caller said otherwise. One route sends a FILE and has to
  // declare the file's own type: content-service sanitises SVG and checks
  // raster by file signature, and both start from the Content-Type it is
  // given. Every existing caller sends JSON and sets nothing, so they are
  // unaffected.
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!noAuth && session?.token) {
    headers.set("Authorization", `Bearer ${session.token}`);
  }

  let response = await fetch(buildUrl(path, requestOptions.params), {
    ...requestOptions,
    headers,
    credentials: "include",
  });

  if (response.status === 401 && !_isRetry && !noAuth) {
    const token = await getFreshAccessToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      response = await fetch(buildUrl(path, requestOptions.params), {
        ...requestOptions,
        headers,
        credentials: "include",
      });
    }
  }

  return handleResponse<T>(response);
}
